import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, TrendingUp, Clock, Grid3x3, Compass } from 'lucide-react';
import Header from '../components/Header';
import Tree from '../components/Tree';
import NotesModal from '../components/NotesModal';
import SuggestModal from '../components/SuggestModal';
import { aiFrameworkTree, legend } from '../data/mock';
import { regroupTree } from '../data/supercategories';
import NewsletterCapture from '../components/NewsletterCapture';
import { fetchSubmissions, fetchHomepage, fetchDiscoveryIndex, API } from '../api/client';
import { logger } from '../lib/logger';

function slugify(text) {
  return (text || '')
    .toString()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
}

const RESOURCE_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'tool', label: 'Tools' },
  { key: 'creator', label: 'Creators' },
  { key: 'company', label: 'Companies' },
  { key: 'discover', label: 'Discovery' },
];

function filterTreeByType(root, typeKey) {
  if (typeKey === 'all') return root;
  // Deep filter: keep subtrees that lead to a leaf whose _route matches or, for
  // 'tool', leaves without _route (regular tools). Non-leaves are kept if any
  // descendant matches.
  const matches = (leaf) => {
    if (typeKey === 'tool') return !leaf._route && !!leaf.url;
    return leaf._route === typeKey;
  };
  const walk = (n) => {
    if (n.children && n.children.length) {
      const kids = n.children.map(walk).filter(Boolean);
      if (kids.length) return { ...n, children: kids };
      return null;
    }
    return matches(n) ? n : null;
  };
  const kids = (root.children || []).map(walk).filter(Boolean);
  return { ...root, children: kids };
}

export default function TreePage() {
  const [query, setQuery] = useState('');
  const [notesOpen, setNotesOpen] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [homepage, setHomepage] = useState({ categories: [], recent: [], popular: [] });
  const [discovery, setDiscovery] = useState({ buckets: [] });
  const [theme, setTheme] = useState(() => localStorage.getItem('ai_fw_theme') || 'dark');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('ai_fw_theme', theme);
  }, [theme]);

  const reloadSubmissions = useCallback(async () => {
    try {
      const data = await fetchSubmissions();
      setSubmissions(data);
    } catch (e) {
      logger.error('TreePage: failed to load submissions', e);
    }
  }, []);

  useEffect(() => {
    reloadSubmissions();
    fetchHomepage().then((d) =>
      setHomepage({
        categories: d.categories || [],
        recent: d.recent || [],
        popular: d.popular || [],
      })
    );
    fetchDiscoveryIndex().then(setDiscovery).catch(() => setDiscovery({ buckets: [] }));
  }, [reloadSubmissions]);

  const toggleTheme = () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'));

  const filteredTree = useMemo(
    () => filterTreeByType(regroupTree(aiFrameworkTree), typeFilter),
    [typeFilter]
  );

  return (
    <div className="page">
      <Header
        query={query}
        setQuery={setQuery}
        theme={theme}
        toggleTheme={toggleTheme}
        onOpenNotes={() => setNotesOpen(true)}
        onOpenSuggest={() => setSuggestOpen(true)}
      />

      <div className="tree-filter-bar" data-testid="tree-filter-bar">
        <span className="tree-filter-label">Show:</span>
        {RESOURCE_FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`tree-filter-chip ${typeFilter === f.key ? 'active' : ''}`}
            onClick={() => setTypeFilter(f.key)}
            data-testid={`tree-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
        <Link to="/discover" className="tree-filter-more">
          <Compass size={11} /> Explore all discovery categories →
        </Link>
      </div>
      <main className="main">
        <div className="tree-desktop-only">
          <Tree
            data={filteredTree}
            query={query}
            submissions={submissions}
            onSubmissionsChange={setSubmissions}
          />
        </div>

        <div className="tree-mobile-only" data-testid="mobile-category-grid">
          <div className="mobile-hero">
            <h1 className="mobile-hero-title">Explore 1,600+ AI tools</h1>
            <p className="mobile-hero-sub">
              Pick a category to browse, or scroll down for trending picks and the Side Hustle Playbook.
            </p>
          </div>
          <div className="mobile-cat-grid">
            {(homepage.categories || []).slice(0, 24).map((c) => (
              <Link
                key={c.slug || c.name}
                to={`/category/${c.slug || slugify(c.name)}`}
                className="mobile-cat-card"
                data-testid={`mobile-cat-${c.slug || slugify(c.name)}`}
              >
                <span className="mobile-cat-name">{c.name}</span>
                <span className="mobile-cat-count">{c.count}</span>
              </Link>
            ))}
          </div>
          {(!homepage.categories || homepage.categories.length === 0) && (
            <p className="supp-empty">Loading categories…</p>
          )}
          <Link to="/discover" className="mobile-cat-more" data-testid="mobile-see-all">
            See all discovery categories →
          </Link>
        </div>
      </main>

      <section className="tree-supp">
        <div className="tree-supp-inner">
          <div className="supp-col">
            <h3 className="supp-title"><TrendingUp size={13} /> Trending</h3>
            {homepage.popular.length === 0 ? (
              <p className="supp-empty">No click data yet. As people explore, popular tools appear here.</p>
            ) : (
              <ol className="supp-list">
                {homepage.popular.slice(0, 8).map((t) => (
                  <li key={t.id}>
                    <Link to={`/tool/${t.slug}`} className="supp-link">{t.name}</Link>
                    <a
                      className="supp-visit"
                      href={`${API}/go/${t.slug}?ref=/`}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Visit"
                    >
                      <ExternalLink size={11} />
                    </a>
                    <span className="supp-count">{t.click_count}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>

          <div className="supp-col">
            <h3 className="supp-title"><Compass size={13} /> Discover</h3>
            <div className="supp-cats">
              {discovery.buckets.slice(0, 9).map((b) => (
                <Link key={b.slug} to={`/discover/${b.slug}`} className="supp-cat-chip" data-testid={`supp-discover-${b.slug}`}>
                  {b.name} <span className="supp-cat-count">{b.count}</span>
                </Link>
              ))}
            </div>
            <div style={{ marginTop: 16 }}>
              <NewsletterCapture source="tree-homepage" compact />
            </div>
          </div>

          <div className="supp-col">
            <h3 className="supp-title"><Clock size={13} /> Recently added</h3>
            <ol className="supp-list">
              {homepage.recent.slice(0, 8).map((t) => (
                <li key={t.id}>
                  <Link to={`/tool/${t.slug}`} className="supp-link">{t.name}</Link>
                  <span className="supp-cat">{t.primary_category}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="supp-col">
            <h3 className="supp-title"><Grid3x3 size={13} /> Categories</h3>
            <div className="supp-cats">
              {homepage.categories.slice(0, 24).map((c) => (
                <Link key={c.slug} to={`/category/${c.slug}`} className="supp-cat-chip">
                  {c.name} <span className="supp-cat-count">{c.count}</span>
                </Link>
              ))}
              {homepage.categories.length > 24 && (
                <Link to="/browse" className="supp-cat-chip more">
                  See all {homepage.categories.length} →
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      <footer className="legend">
        {legend.map((l) => (
          <span key={l.code} className={`legend-item chip-${l.code.replace(/[()]/g, '')}`}>
            <b>{l.code}</b> {l.desc}
          </span>
        ))}
        <span className="legend-item">
          <b className="star">★</b> Community-submitted
        </span>
        <span className="legend-item muted">
          Leaves link to tool pages · Every outbound click is tracked
        </span>
        <span className="legend-item">
          <Link to="/disclosure" data-testid="tree-disclosure-link">Disclosure</Link>
          {' · '}
          <Link to="/submit">Submit a tool</Link>
        </span>
      </footer>

      <NotesModal open={notesOpen} onClose={() => setNotesOpen(false)} />
      <SuggestModal
        open={suggestOpen}
        onClose={() => setSuggestOpen(false)}
        onCreated={() => reloadSubmissions()}
      />
    </div>
  );
}
